/**
 * Timing.jsx
 * Dynamic clinic_settings API Version
 */

import { useEffect, useState } from "react";
import { apiFetch } from "../helpers";

export default function Timing({
  onBookClick,
}) {
  const [clinic, setClinic] = useState({
    clinic_name: "",
    email: "",
    phone: "",
    full_address: "",
    mon_fri_open: "09:00:00",
    mon_fri_close: "20:00:00",
    saturday_open: "09:00:00",
    saturday_close: "16:00:00",
    sunday_closed: 1,
  });

  useEffect(() => {
    loadClinic();
  }, []);

  const loadClinic = async () => {
    try {
      const json = await apiFetch(
        `${process.env.REACT_APP_GET_CLINIC}`
      );

      if (json.status && json.data) {
        setClinic(json.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const formatTime = (time) => {
    if (!time) return "";

    const [h, m] = time.split(":");
    let hour = parseInt(h);
    const ampm =
      hour >= 12 ? "PM" : "AM";

    hour = hour % 12 || 12;

    return `${hour}:${m} ${ampm}`;
  };

  const schedule = [
    {
      day: "Monday",
      hours: `${formatTime(
        clinic.mon_fri_open
      )} – ${formatTime(
        clinic.mon_fri_close
      )}`,
      open: true,
    },
    {
      day: "Tuesday",
      hours: `${formatTime(
        clinic.mon_fri_open
      )} – ${formatTime(
        clinic.mon_fri_close
      )}`,
      open: true,
    },
    {
      day: "Wednesday",
      hours: `${formatTime(
        clinic.mon_fri_open
      )} – ${formatTime(
        clinic.mon_fri_close
      )}`,
      open: true,
    },
    {
      day: "Thursday",
      hours: `${formatTime(
        clinic.mon_fri_open
      )} – ${formatTime(
        clinic.mon_fri_close
      )}`,
      open: true,
    },
    {
      day: "Friday",
      hours: `${formatTime(
        clinic.mon_fri_open
      )} – ${formatTime(
        clinic.mon_fri_close
      )}`,
      open: true,
    },
    {
      day: "Saturday",
      hours: `${formatTime(
        clinic.saturday_open
      )} – ${formatTime(
        clinic.saturday_close
      )}`,
      open: true,
    },
    {
      day: "Sunday",
      hours:
        clinic.sunday_closed == 1
          ? "Closed"
          : `${formatTime(
              clinic.mon_fri_open
            )} – ${formatTime(
              clinic.mon_fri_close
            )}`,
      open:
        clinic.sunday_closed == 1
          ? false
          : true,
    },
  ];

  const today =
    [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ][new Date().getDay()];

  return (
    <section
      id="timing"
      style={S.section}
    >
      <div style={S.bg1}></div>
      <div style={S.bg2}></div>

      <div style={S.container}>
        {/* Heading */}
        <div style={S.head}>
          <span style={S.badge}>
            Working Hours
          </span>

          <h2 style={S.title}>
            Visit Us at Your
            <br />
            <span
              style={{
                color: "#1D9E75",
              }}
            >
              Convenient Time
            </span>
          </h2>

          <p style={S.sub}>
            We’re open 6 days a week
            with flexible morning and
            evening slots.
          </p>
        </div>

        <div
          className="timing-grid"
          style={S.grid}
        >
          {/* Left */}
          <div>
            <div style={S.table}>
              {schedule.map((s, i) => {
                const isToday =
                  s.day === today;

                return (
                  <div
                    key={i}
                    style={{
                      ...S.row,
                      background:
                        isToday
                          ? "rgba(29,158,117,.12)"
                          : "transparent",
                      borderBottom:
                        i <
                        schedule.length -
                          1
                          ? "1px solid rgba(255,255,255,.06)"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        display:
                          "flex",
                        alignItems:
                          "center",
                        gap: 10,
                      }}
                    >
                      {isToday && (
                        <div
                          style={
                            S.dot
                          }
                        />
                      )}

                      <span
                        style={{
                          color:
                            "#fff",
                          fontWeight:
                            isToday
                              ? 700
                              : 500,
                        }}
                      >
                        {s.day}
                      </span>

                      {isToday && (
                        <span
                          style={
                            S.today
                          }
                        >
                          Today
                        </span>
                      )}
                    </div>

                    <span
                      style={{
                        color:
                          s.open
                            ? isToday
                              ? "#1D9E75"
                              : "rgba(255,255,255,.75)"
                            : "rgba(255,255,255,.35)",
                        fontFamily:
                          s.open
                            ? "monospace"
                            : "inherit",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {s.hours}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Emergency */}
            <div style={S.emg}>
              <span>🚨</span>
              <span>
                Dental emergencies?
                Call us at{" "}
                <strong
                  style={{
                    color: "#fff",
                  }}
                >
                  {clinic.phone}
                </strong>
              </span>
            </div>
          </div>

          {/* Right */}
          <div>
            {/* Map */}
            <div style={S.map}>
              <span
                style={{
                  fontSize: 40,
                }}
              >
                📍
              </span>

              <p style={S.mapText}>
                {clinic.full_address}
              </p>

              <a
                href={`https://maps.google.com?q=${encodeURIComponent(
                  clinic.full_address
                )}`}
                target="_blank"
                rel="noreferrer"
                style={S.mapLink}
              >
                View on Google Maps →
              </a>
            </div>

            {/* Contact */}
            <div
              style={{
                display: "grid",
                gap: 12,
              }}
            >
              {[
                {
                  icon: "📞",
                  label: "Phone",
                  value:
                    clinic.phone,
                },
                {
                  icon: "📧",
                  label: "Email",
                  value:
                    clinic.email,
                },
                {
                  icon: "📍",
                  label:
                    "Address",
                  value:
                    clinic.full_address,
                },
              ].map((item) => (
                <div
                  key={
                    item.label
                  }
                  style={
                    S.info
                  }
                >
                  <span
                    style={{
                      fontSize: 18,
                    }}
                  >
                    {
                      item.icon
                    }
                  </span>

                  <div>
                    <div
                      style={
                        S.infoLbl
                      }
                    >
                      {
                        item.label
                      }
                    </div>

                    <div
                      style={
                        S.infoVal
                      }
                    >
                      {
                        item.value
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={
                onBookClick
              }
              style={S.btn}
            >
              📅 Book an
              Appointment
              Now
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width:768px){
          .timing-grid{
            grid-template-columns:1fr!important;
            gap:32px!important;
          }
        }
      `}</style>
    </section>
  );
}

const S = {
  section: {
    background: "#0A1628",
    padding: "90px 0 80px",
    fontFamily:
      "'Nunito','Segoe UI',sans-serif",
    position: "relative",
    overflow: "hidden",
  },

  bg1: {
    position: "absolute",
    width: 400,
    height: 400,
    borderRadius: "50%",
    border:
      "1px solid rgba(29,158,117,.1)",
    top: -100,
    right: -100,
  },

  bg2: {
    position: "absolute",
    width: 250,
    height: 250,
    borderRadius: "50%",
    border:
      "1px solid rgba(29,158,117,.07)",
    bottom: -60,
    left: -60,
  },

  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 1.5rem",
    position: "relative",
  },

  head: {
    textAlign: "center",
    marginBottom: 56,
  },

  badge: {
    display: "inline-block",
    background:
      "rgba(29,158,117,.15)",
    color: "#5DCAA5",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    padding: "5px 16px",
    borderRadius: 20,
    marginBottom: 14,
  },

  title: {
    fontSize:
      "clamp(1.6rem,4vw,2.5rem)",
    fontWeight: 800,
    color: "#fff",
    marginBottom: 16,
  },

  sub: {
    color: "rgba(255,255,255,.55)",
    fontSize: 16,
    maxWidth: 440,
    margin: "0 auto",
    lineHeight: 1.7,
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "1fr 1fr",
    gap: 48,
    alignItems: "start",
  },

  table: {
    background:
      "rgba(255,255,255,.04)",
    borderRadius: 16,
    overflow: "hidden",
    border:
      "1px solid rgba(255,255,255,.08)",
  },

  row: {
    display: "flex",
    justifyContent:
      "space-between",
    alignItems: "center",
    padding: "14px 20px",
  },

  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#1D9E75",
  },

  today: {
    fontSize: 10,
    background: "#1D9E75",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 10,
    fontWeight: 700,
  },

  emg: {
    marginTop: 16,
    padding: "12px 16px",
    background:
      "rgba(232,50,60,.1)",
    borderRadius: 10,
    border:
      "1px solid rgba(232,50,60,.25)",
    display: "flex",
    gap: 10,
    color: "rgba(255,255,255,.75)",
    fontSize: 13,
  },

  map: {
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 20,
    border:
      "1px solid rgba(255,255,255,.08)",
    height: 220,
    background:
      "rgba(255,255,255,.04)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 12,
    padding: 20,
  },

  mapText: {
    color: "rgba(255,255,255,.5)",
    fontSize: 13,
    margin: 0,
    textAlign: "center",
  },

  mapLink: {
    fontSize: 12,
    color: "#1D9E75",
    fontWeight: 700,
    textDecoration: "none",
  },

  info: {
    display: "flex",
    gap: 14,
    padding: "12px 16px",
    background:
      "rgba(255,255,255,.04)",
    borderRadius: 10,
    border:
      "1px solid rgba(255,255,255,.07)",
  },

  infoLbl: {
    fontSize: 10,
    color: "rgba(255,255,255,.4)",
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginBottom: 2,
  },

  infoVal: {
    fontSize: 13,
    color: "rgba(255,255,255,.8)",
    fontWeight: 500,
  },

  btn: {
    marginTop: 20,
    width: "100%",
    background: "#E8323C",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: 13,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
};